/**
 * VoicePicker - 音色选择器组件
 * 
 * 使用 Popover + Command 实现，支持：
 * - 触发器显示头像 + 名称
 * - 下拉列表显示完整音色信息
 * - 搜索过滤
 * - 男/女头像图标
 */

import { useState, useMemo } from 'react'
import { Check, ChevronsUpDown, User, UserRound } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Badge } from '@/shared/components/ui/badge'
import type { TTSVoice } from '@/modules/agent/services/voice-config-api'

interface VoicePickerProps {
  /** 当前选中的音色 ID */
  value?: string
  /** 选择变化回调 */
  onValueChange: (value: string | undefined) => void
  /** 可选的音色列表 */
  voices: TTSVoice[]
  /** 占位文本 */
  placeholder?: string
  /** 是否显示"使用默认"选项（用于 Agent 节点） */
  showDefaultOption?: boolean
  /** "使用默认"选项的文本 */
  defaultOptionText?: string
  /** 是否禁用 */
  disabled?: boolean
  /** 自定义类名 */
  className?: string
}

/**
 * 获取性别头像组件
 */
const GenderAvatar = ({ gender, className }: { gender: string; className?: string }) => {
  if (gender === 'male') {
    return (
      <div className={cn("flex items-center justify-center w-8 h-8 rounded-full bg-blue-100", className)}>
        <User className="h-4 w-4 text-blue-600" />
      </div>
    )
  }
  if (gender === 'female') {
    return (
      <div className={cn("flex items-center justify-center w-8 h-8 rounded-full bg-pink-100", className)}>
        <UserRound className="h-4 w-4 text-pink-600" />
      </div>
    )
  }
  // neutral
  return (
    <div className={cn("flex items-center justify-center w-8 h-8 rounded-full bg-slate-100", className)}>
      <User className="h-4 w-4 text-slate-600" />
    </div>
  )
}

/**
 * 音色信息卡片（下拉列表中显示）
 */
const VoiceCard = ({ voice, isSelected }: { voice: TTSVoice; isSelected: boolean }) => {
  return (
    <div className="flex items-start gap-3 w-full">
      <GenderAvatar gender={voice.gender} className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        {/* 第一行：名称 + 默认徽章 */}
        <div className="flex items-center gap-2">
          <span className="font-medium truncate">{voice.name}</span>
          {voice.default && (
            <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4 shrink-0">
              默认
            </Badge>
          )}
        </div>
        {/* 第二行：描述 + 年龄范围 */}
        {(voice.description || voice.age_range) && (
          <div className="text-xs text-slate-500 truncate">
            {voice.description}
            {voice.description && voice.age_range && ' · '}
            {voice.age_range}
          </div>
        )}
        {/* 第三行：适用场景 */}
        {voice.scenarios && voice.scenarios.length > 0 && (
          <div className="text-xs text-slate-400 truncate">
            📍 {voice.scenarios.join('、')}
          </div>
        )}
        {/* 第四行：支持语言 */}
        {voice.languages && voice.languages.length > 0 && (
          <div className="text-xs text-slate-400 truncate">
            🌐 {voice.languages.join('、')}
          </div>
        )}
      </div>
      {/* 选中标记 */}
      <Check
        className={cn(
          "h-4 w-4 shrink-0 mt-1",
          isSelected ? "opacity-100 text-green-600" : "opacity-0"
        )}
      />
    </div>
  )
}

export function VoicePicker({
  value,
  onValueChange,
  voices,
  placeholder = "选择音色",
  showDefaultOption = false,
  defaultOptionText = "使用默认音色",
  disabled = false,
  className,
}: VoicePickerProps) {
  const [open, setOpen] = useState(false)

  // 找到当前选中的音色
  const selectedVoice = useMemo(() => {
    if (!value || value === '__default__') return null
    return voices.find((v) => v.id === value)
  }, [value, voices])

  // 是否选中了"使用默认"
  const isDefaultSelected = showDefaultOption && (!value || value === '__default__')

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between h-auto min-h-9 py-2", className)}
        >
          {isDefaultSelected ? (
            <span className="text-slate-500">{defaultOptionText}</span>
          ) : selectedVoice ? (
            <div className="flex items-center gap-2">
              <GenderAvatar gender={selectedVoice.gender} className="w-6 h-6" />
              <span className="truncate">{selectedVoice.name}</span>
            </div>
          ) : (
            <span className="text-slate-500">{placeholder}</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[340px] p-0" align="start">
        <Command>
          <CommandInput placeholder="搜索音色..." />
          <CommandList>
            <CommandEmpty>未找到匹配的音色</CommandEmpty>
            <CommandGroup>
              {/* 默认选项（可选） */}
              {showDefaultOption && (
                <CommandItem
                  value="__default__"
                  onSelect={() => {
                    onValueChange(undefined)
                    setOpen(false)
                  }}
                  className="py-2"
                >
                  <div className="flex items-center gap-3 w-full">
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 shrink-0">
                      <User className="h-4 w-4 text-slate-400" />
                    </div>
                    <span className="text-slate-500 flex-1">{defaultOptionText}</span>
                    <Check
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isDefaultSelected ? "opacity-100 text-green-600" : "opacity-0"
                      )}
                    />
                  </div>
                </CommandItem>
              )}
              {/* 音色列表 */}
              {voices.map((voice) => (
                <CommandItem
                  key={voice.id}
                  value={`${voice.id} ${voice.name} ${voice.description || ''}`}
                  onSelect={() => {
                    onValueChange(voice.id)
                    setOpen(false)
                  }}
                  className="py-2"
                >
                  <VoiceCard voice={voice} isSelected={value === voice.id} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}

export default VoicePicker
